import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { projectReportAPI } from '../api';

const STATUS_LABELS = {
  trial: 'Trial', recipe_confirmed: 'Recipe Confirmed',
  commercial_trial: 'Commercial Trial', launched: 'Launched',
};

const STATUS_COLORS = {
  trial: '#e67e00', recipe_confirmed: '#2e7d32',
  commercial_trial: '#1a56db', launched: '#7B2D6B',
};

const STATUS_BG = {
  trial: '#fff3e0', recipe_confirmed: '#f0faf0',
  commercial_trial: '#e8f0fe', launched: '#f3e8ff',
};

export default function ProjectReport({ project }) {
  const [data, setData] = useState(null);
  const [showAddCost, setShowAddCost] = useState(false);
  const [editRndCost, setEditRndCost] = useState(null); // { product_type, current_cost, product_name }
  const [rndCostValue, setRndCostValue] = useState('');
  const [costForm, setCostForm] = useState({ description: '', category: '', amount: '', cost_date: new Date().toISOString().split('T')[0], product_id: '', cost_type: 'market_sample' });

  const load = useCallback(() => {
    projectReportAPI.getReport(project.id).then(r => setData(r.data));
  }, [project.id]);

  useEffect(() => { load(); }, [project.id]);

  const saveRndCost = async () => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    // Find the settings id for this product type
    const resp = await fetch(base + '/settings/product_type');
    const settings = await resp.json();
    const setting = settings.find(s => s.value === editRndCost.product_type);
    if (!setting) return toast.error('Product type not found in settings');
    await fetch(base + '/settings/' + setting.id, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: setting.value, description: setting.description || '', dev_cost: parseFloat(rndCostValue) || 0 })
    });
    toast.success('R&D cost updated');
    setEditRndCost(null);
    load();
  };

  const addCost = async () => {
    if (!costForm.description || !costForm.amount) return toast.error('Description and amount required');
    await projectReportAPI.addAdditionalCost(project.id, costForm);
    toast.success('Cost added');
    setShowAddCost(false);
    setCostForm({ description: '', category: '', amount: '', cost_date: new Date().toISOString().split('T')[0], product_id: '', cost_type: 'market_sample' });
    load();
  };

  const deleteCost = async (id) => {
    await projectReportAPI.deleteAdditionalCost(id);
    load();
  };

  const fmt = (n) => `AED ${(n||0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const printPDF = () => {
    if (!data) return;
    const win = window.open('', '_blank');
    const userName = (() => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.name || u?.username || '—'; } catch { return '—'; } })();
    const html = generatePrintHTML(data, fmt, userName);
    win.document.write(html);
    win.document.close();
    win.focus();
    // Preview only - user prints manually
  };

  if (!data) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <div className="flex-between mb-6">
        <div className="text-muted" style={{ fontSize: 13 }}>
          {data.products.length} products · {fmt(data.trialTotal)} trial costs · {fmt((data.additionalTotal||0)+(data.categoryDevTotal||0))} additional
        </div>
        <button className="btn btn-primary" onClick={printPDF}>⬇ Download / Print PDF</button>
      </div>

      {/* Trial Costs */}
      <div className="card mb-6">
        <div className="flex-between mb-4">
          <div className="card-title" style={{ margin: 0 }}>Product Trial Costs</div>
          <div className="cost-value mono">{fmt(data.trialTotal)}</div>
        </div>
        {data.products.length === 0 ? (
          <div className="empty-state"><p>No products in this project yet.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product Code</th>
                  <th>Product Name</th>
                  <th>Status</th>
                  <th className="text-right">Versions</th>
                  <th className="text-right">Trials</th>
                  <th className="text-right">Trial Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map(p => (
                  <tr key={p.id}>
                    <td className="mono">{p.product_code}</td>
                    <td style={{ fontWeight: 500 }}>{p.name}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                        fontSize: 10, fontWeight: 600,
                        background: STATUS_BG[p.status] || '#f5f5f5',
                        color: STATUS_COLORS[p.status] || '#555',
                      }}>{STATUS_LABELS[p.status] || p.status}</span>
                    </td>
                    <td className="text-right mono">{p.version_count}</td>
                    <td className="text-right mono">{p.trial_count}</td>
                    <td className="text-right cost-value">{fmt(p.trial_cost)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--accent)' }}>Total</td>
                  <td className="text-right cost-value" style={{ fontWeight: 700, fontSize: 13 }}>{fmt(data.trialTotal)}</td>

                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Additional Costs */}
      <div className="card mb-6">
        <div className="flex-between mb-4">
          <div className="card-title" style={{ margin: 0 }}>Additional Project Costs</div>
          <div className="flex gap-2" style={{ alignItems: 'center' }}>
            <div className="cost-value mono">{fmt((data.additionalTotal||0)+(data.categoryDevTotal||0))}</div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAddCost(true)}>+ Add Cost</button>
          </div>
        </div>
        {data.additionalCosts.length === 0 && data.products.filter(p => p.category_dev_cost > 0).length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>No additional costs yet. Add travel, samples, testing, etc.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Linked Product</th>
                  <th>Date</th>
                  <th className="text-right">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...data.additionalCosts, ...data.products.filter(p => p.category_dev_cost > 0).map(p => ({ id: 'rnd-'+p.id, description: 'R&D Fixed Cost — '+p.name, cost_type: 'rnd_cost', product_id: p.id, cost_date: 'Auto', amount: p.category_dev_cost, _auto: true, _product_type: p.product_type, _product_name: p.name }))].map(c => (
                  <tr key={c.id}>
                    <td style={{ fontStyle: c._auto ? 'italic' : 'normal', color: c._auto ? 'var(--accent)' : 'inherit' }}>{c.description}</td>
                    <td><span style={{ fontSize: 11, background: c.cost_type === 'rnd_cost' ? 'var(--accent-dim)' : '#fff3e0', color: c.cost_type === 'rnd_cost' ? 'var(--accent)' : '#e67e00', padding: '2px 7px', borderRadius: 10, fontWeight: 600 }}>{c.cost_type === 'rnd_cost' ? 'R&D Cost' : 'Market Sample'}</span></td>
                    <td className="text-muted" style={{ fontSize: 12 }}>{c.product_id ? (data.products.find(p => p.id === c.product_id || p.id === parseInt(c.product_id))?.name || '—') : <span style={{ fontStyle: 'italic', fontSize: 11 }}>Shared</span>}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{c.cost_date || '—'}</td>
                    <td className="text-right cost-value">{fmt(c.amount)}</td>
                    <td>{c._auto
                      ? <button className="btn btn-secondary btn-sm" onClick={() => { setEditRndCost({ product_type: c._product_type, product_name: c._product_name }); setRndCostValue(c.amount); }}>Edit</button>
                      : <button className="btn btn-danger btn-sm" onClick={() => deleteCost(c.id)}>×</button>
                    }</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, color: 'var(--amber)' }}>Total Additional</td>
                  <td className="text-right" style={{ fontWeight: 700, color: 'var(--amber)', fontSize: 13 }}>{fmt((data.additionalTotal||0)+(data.categoryDevTotal||0))}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grand Total */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderRight: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Trial Costs</div>
          <div className="cost-value mono" style={{ fontSize: 16, fontWeight: 700 }}>{fmt(data.trialTotal)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{data.products.length} products</div>
        </div>
        <div style={{ padding: '14px 18px', borderRight: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Additional Costs</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--amber)', fontFamily: 'DM Mono, monospace' }}>{fmt((data.additionalTotal||0)+(data.categoryDevTotal||0))}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{data.additionalCosts.length} items</div>
        </div>
        <div style={{ padding: '14px 18px', background: 'var(--accent-dim)' }}>
          <div style={{ fontSize: 10, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Total Project Cost</div>
          <div className="cost-value mono" style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.grandTotal||0)}</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Combined R&D spend</div>
        </div>
      </div>

      {/* Add Cost Modal */}
      {editRndCost && (
        <div className="modal-overlay" onClick={() => setEditRndCost(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <div className="modal-title">Edit R&D Fixed Cost</div>
              <button className="modal-close" onClick={() => setEditRndCost(null)}>×</button>
            </div>
            <div style={{ padding: '16px 0' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                Updating R&D cost for product type: <strong style={{ color: 'var(--accent)' }}>{editRndCost.product_type}</strong><br/>
                <span style={{ fontSize: 11 }}>This will apply to all products of this type across all projects.</span>
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>R&D Fixed Cost (AED)</label>
              <input type="number" step="0.01" value={rndCostValue} onChange={e => setRndCostValue(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 16, fontWeight: 600 }} autoFocus />
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveRndCost}>Save</button>
              <button className="btn btn-secondary" onClick={() => setEditRndCost(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAddCost && (
        <div className="modal-overlay" onClick={() => setShowAddCost(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Additional Cost</div>
              <button className="modal-close" onClick={() => setShowAddCost(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Description *</label>
                <input placeholder="e.g. Market visit — Dubai Supermarkets" value={costForm.description} onChange={e => setCostForm({ ...costForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Cost Type *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['market_sample','🛒 Market Sample'],['rnd_cost','🧪 R&D Cost']].map(([val, lbl]) => (
                    <button key={val} type="button" className={'btn btn-sm ' + (costForm.cost_type===val?'btn-primary':'btn-secondary')} onClick={() => setCostForm({...costForm, cost_type: val})}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Link to Product</label>
                <select value={costForm.product_id} onChange={e => setCostForm({ ...costForm, product_id: e.target.value })}>
                  <option value="">All products (shared equally)</option>
                  {data.products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={costForm.category} onChange={e => setCostForm({ ...costForm, category: e.target.value })}>
                  <option value="">Select category</option>
                  <option>Travel</option>
                  <option>Market Samples</option>
                  <option>Testing</option>
                  <option>Consultancy</option>
                  <option>Packaging</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={costForm.cost_date} onChange={e => setCostForm({ ...costForm, cost_date: e.target.value })} />
              </div>
              <div className="form-group full">
                <label>Amount (AED) *</label>
                <input type="number" step="0.01" placeholder="0.00" value={costForm.amount} onChange={e => setCostForm({ ...costForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddCost(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addCost}>Add Cost</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function calcDuration(start, end) {
  if (!start) return '—';
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const days = Math.floor((e - s) / (1000*60*60*24));
  if (days < 0) return '—';
  const months = Math.floor(days/30);
  const remDays = days%30;
  if (months===0) return days+' days';
  if (remDays===0) return months+' month'+(months>1?'s':'');
  return months+' month'+(months>1?'s':'')+' '+remDays+' day'+(remDays>1?'s':'');
}

function generatePrintHTML(data, fmt, userName) {
  const { project, products, additionalCosts, trialTotal, additionalTotal, categoryDevTotal, grandTotal } = data;
  const today = new Date().toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' });
  const totalAdditional = (additionalTotal||0) + (categoryDevTotal||0);

  const statusColors = { trial: '#e67e00', recipe_confirmed: '#2e7d32', commercial_trial: '#1a56db', launched: '#7B2D6B' };
  const statusBg = { trial: '#fff3e0', recipe_confirmed: '#f0faf0', commercial_trial: '#e8f0fe', launched: '#f3e8ff' };
  const statusLabels = { trial: 'Trial', recipe_confirmed: 'Recipe Confirmed', commercial_trial: 'Commercial Trial', launched: 'Launched' };

  const productRows = products.map((p, i) => `
    <tr style="${i % 2 === 1 ? 'background:#fdf9fc;' : ''}">
      <td style="font-family:monospace;font-size:9px;color:#a080a0;">${p.product_code}</td>
      <td style="font-weight:500;">${p.name}</td>
      <td><span style="display:inline-block;padding:1px 8px;border-radius:20px;font-size:8px;font-weight:600;background:${statusBg[p.status]||'#f5f5f5'};color:${statusColors[p.status]||'#555'}">${statusLabels[p.status]||p.status}</span></td>
      <td style="text-align:center;">${p.version_count}</td>
      <td style="text-align:center;">${p.trial_count}</td>
      <td style="text-align:right;font-weight:600;color:#7B2D6B;">${fmt(p.trial_cost)}</td>
    </tr>
  `).join('');

  const additionalRows = [
    ...additionalCosts.map((c, i) => `
      <tr style="${i % 2 === 1 ? 'background:#fdf9fc;' : ''}">
        <td>${c.description}</td>
        <td style="font-size:9px;padding:3px 8px;"><span style="background:${c.cost_type==='rnd_cost'?'#f3e8ff':'#fff3e0'};color:${c.cost_type==='rnd_cost'?'#7B2D6B':'#e67e00'};padding:1px 6px;border-radius:8px;font-weight:600;">${c.cost_type==='rnd_cost'?'R&D Cost':'Market Sample'}</span></td>
        <td style="color:#888;font-size:9px;">${c.product_id ? (products.find(p=>p.id===c.product_id)?.name||'—') : 'Shared'}</td>
        <td style="font-family:monospace;font-size:9px;color:#888;">${c.cost_date || '—'}</td>
        <td style="text-align:right;font-weight:600;color:#E8541A;">${fmt(c.amount)}</td>
      </tr>
    `),
    ...products.filter(p => p.category_dev_cost > 0).map((p, i) => `
      <tr style="background:#f9f3ff;">
        <td style="font-style:italic;color:#7B2D6B;">R&D Fixed Cost — ${p.name}</td>
        <td style="font-size:9px;padding:3px 8px;"><span style="background:#f3e8ff;color:#7B2D6B;padding:1px 6px;border-radius:8px;font-weight:600;">R&D Cost</span></td>
        <td style="color:#888;font-size:9px;">${p.name}</td>
        <td style="font-family:monospace;font-size:9px;color:#888;">Auto</td>
        <td style="text-align:right;font-weight:600;color:#7B2D6B;">${fmt(p.category_dev_cost)}</td>
      </tr>
    `)
  ].join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Inter',Arial,sans-serif;background:#fff;padding:28px;color:#1a1a1a;font-size:11px;}
    .top-bar{height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:2px 2px 0 0;margin-bottom:0;}
    .card{border:0.5px solid #e4d4e0;border-radius:3px;overflow:hidden;margin-bottom:16px;}
    .card-header{display:flex;justify-content:space-between;align-items:center;padding:6px 12px;border-bottom:1px solid #e4d4e0;}
    .sec-title{font-size:7px;letter-spacing:0.2em;text-transform:uppercase;font-weight:700;}
    table{width:100%;border-collapse:collapse;font-size:10px;}
    th{padding:5px 8px;text-align:left;font-size:7px;letter-spacing:0.1em;text-transform:uppercase;color:#c0a0b8;font-weight:600;border-bottom:1px solid #e4d4e0;}
    td{padding:5px 8px;border-bottom:0.5px solid #f5edf3;}
    .subtotal td{border-top:1px solid #e4d4e0;font-weight:700;background:#f9f3f8;}
    .summary{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #e4d4e0;border-radius:3px;overflow:hidden;margin-bottom:14px;}
    .sc{padding:10px 14px;border-right:1px solid #e4d4e0;}
    .sc:last-child{border-right:none;background:#7B2D6B;}
    .sl{font-size:7px;letter-spacing:0.14em;text-transform:uppercase;color:#c0a0b8;margin-bottom:3px;}
    .sc:last-child .sl{color:rgba(255,255,255,0.6);}
    .sv{font-size:14px;font-weight:700;color:#7B2D6B;}
    .sc:last-child .sv{color:#fff;font-size:15px;}
    .ssub{font-size:8px;color:#c0a0b8;margin-top:1px;}
    .sc:last-child .ssub{color:rgba(255,255,255,0.5);}
    .footer-row{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #e4d4e0;border-radius:3px;overflow:hidden;}
    .fc{padding:8px 12px;border-right:1px solid #e4d4e0;}
    .fc:last-child{border-right:none;background:#7B2D6B;}
    .fl{font-size:7px;letter-spacing:0.14em;text-transform:uppercase;color:#c0a0b8;margin-bottom:3px;}
    .fc:last-child .fl{color:rgba(255,255,255,0.55);}
    .fv{font-size:10px;font-weight:600;color:#333;}
    .fc:last-child .fv{color:#fff;}
    @media print{body{padding:16px;} @page{margin:0; size:A4;} body{padding:20px 28px;}}
  </style></head><body>
  <div class="top-bar"></div>
  <div style="padding:12px 0 10px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #f0e8ed;margin-bottom:12px;">
    <div>
      <div style="font-size:6.5px;letter-spacing:0.22em;text-transform:uppercase;color:#E8541A;margin-bottom:3px;">Project Cost Report</div>
      <div style="font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:#7B2D6B;">Kallada Food Industries LLC</div>
      <div style="font-size:8px;color:#c0a0b8;margin-top:1px;">Dubai, United Arab Emirates</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:26px;font-weight:800;color:#7B2D6B;letter-spacing:-0.02em;position:relative;display:inline-block;">BGH<span style="position:absolute;top:1px;right:-9px;width:7px;height:7px;background:#E8541A;border-radius:1.5px;display:block;"></span></div>
      <div style="font-size:6px;letter-spacing:0.16em;text-transform:uppercase;color:#E8541A;font-weight:600;margin-top:3px;">Bettergrow Holding</div>
    </div>
  </div>
  <div style="background:#f9f3f8;border:1px solid #ede0ea;border-radius:3px;padding:8px 14px;display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;">
    <div style="font-size:13px;font-weight:700;color:#7B2D6B;">${project.name}</div>
    <div style="display:flex;gap:20px;">
      <div style="text-align:right;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Code</div><div style="font-family:monospace;font-size:10px;font-weight:600;color:#555;">${project.project_code}</div></div>
      <div style="text-align:right;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Department</div><div style="font-size:10px;font-weight:600;color:#333;">${project.department||'—'}</div></div>
      <div style="text-align:right;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;">Report Date</div><div style="font-size:10px;font-weight:600;color:#333;">${today}</div></div>
    </div>
  </div>
  <div class="card">
    <div class="card-header" style="background:#f9f3f8;">
      <div class="sec-title" style="color:#7B2D6B;">Product Trial Costs</div>
      <div style="font-size:10px;font-weight:700;color:#7B2D6B;">${fmt(trialTotal)}</div>
    </div>
    <table>
      <thead><tr><th style="width:85px;">Code</th><th>Product Name</th><th style="width:110px;">Status</th><th style="width:55px;text-align:center;">Versions</th><th style="width:55px;text-align:center;">Trials</th><th style="width:110px;text-align:right;">Trial Cost</th></tr></thead>
      <tbody>
        ${productRows}
        <tr class="subtotal"><td colspan="5" style="text-align:right;color:#7B2D6B;font-size:9px;letter-spacing:0.06em;text-transform:uppercase;">Total Trial Cost</td><td style="text-align:right;color:#7B2D6B;font-size:11px;">${fmt(trialTotal)}</td></tr>
      </tbody>
    </table>
  </div>
  <div class="card">
    <div class="card-header" style="background:#fff3ee;">
      <div class="sec-title" style="color:#E8541A;">Additional Project Costs</div>
      <div style="font-size:10px;font-weight:700;color:#E8541A;">${fmt(totalAdditional)}</div>
    </div>
    <table>
      <thead><tr><th>Description</th><th style="width:90px;">Type</th><th style="width:110px;">Linked Product</th><th style="width:70px;">Date</th><th style="width:110px;text-align:right;">Amount</th></tr></thead>
      <tbody>
        ${additionalRows || '<tr><td colspan="4" style="text-align:center;color:#ccc;font-style:italic;padding:12px;">No additional costs recorded</td></tr>'}
        <tr class="subtotal"><td colspan="4" style="text-align:right;color:#E8541A;font-size:9px;letter-spacing:0.06em;text-transform:uppercase;">Total Additional</td><td style="text-align:right;color:#E8541A;font-size:11px;">${fmt(totalAdditional)}</td></tr>
      </tbody>
    </table>
  </div>
  <div class="summary">
    <div class="sc"><div class="sl">Trial Costs</div><div class="sv">${fmt(trialTotal)}</div><div class="ssub">${products.length} products</div></div>
    <div class="sc"><div class="sl">Additional Costs</div><div class="sv" style="color:#E8541A;">${fmt(totalAdditional)}</div><div class="ssub">${additionalCosts.length} items</div></div>
    <div class="sc"><div class="sl">Total Project Cost</div><div class="sv">${fmt(grandTotal)}</div><div class="ssub">Combined R&D spend</div></div>
  </div>
  <div class="footer-row">
    <div class="fc"><div class="fl">Prepared By</div><div class="fv">${userName}</div></div>
    <div class="fc"><div class="fl">Approved By</div><div class="fv">&nbsp;</div></div>
    <div class="fc"><div class="fl">Project Status</div><div class="fv">${data.project.status}</div></div>
  </div>
  <div style="margin-top:10px;display:flex;justify-content:space-between;">
    <span style="font-size:7px;color:#d0bfca;letter-spacing:0.07em;text-transform:uppercase;">Kallada Food Industries LLC · Confidential R&D Document</span>
    <span style="font-size:7px;color:#d0bfca;letter-spacing:0.07em;text-transform:uppercase;">Generated by NPD Tracker</span>
  </div>
  <div style="height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:0 0 2px 2px;margin-top:10px;"></div>
  </body></html>`;
}
